import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Monitor as MonitorIcon, Zap, Briefcase, ArrowLeftRight, Camera, Mic, Pencil, MessageSquare, ClipboardList, Info,
  Maximize, Minimize, PhoneOff, Loader2, WifiOff, X, Keyboard, Lock, RotateCcw, EyeOff, ClipboardPaste, Circle, Pause, Square, Save, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRemoteSession } from "@/lib/useRemoteSession";
import { useVoice } from "@/lib/useVoice";
import { RemoteCanvas } from "@/components/RemoteCanvas";
import { ChatPanel } from "@/components/ChatPanel";
import { FilesPanel } from "@/components/FilesPanel";
import { CommandPanel } from "@/components/CommandPanel";
import { ClipboardPanel } from "@/components/ClipboardPanel";
import { AudioControls } from "@/components/AudioControls";

const MESSAGES = {
  connecting: "Connexion au serveur…",
  waiting: "En attente de l'image de la machine…",
  offline: "La machine est hors ligne.",
  closed: "Session terminée.",
  denied: "Accès refusé.",
  unpaid: "Essai terminé : un abonnement est nécessaire pour se connecter.",
  busy: "Toutes vos licences sont utilisées : une prise en main par licence à la fois. Fermez une session en cours ou ajoutez une licence.",
};
const STATE_LABEL = { live: "Connecté", connecting: "Connexion", waiting: "En attente", offline: "Hors ligne", closed: "Terminée", reconnecting: "Reconnexion…", denied: "Refusé", unpaid: "Abonnement requis", busy: "Licences occupées" };
const TOOLS = [
  ["view", MonitorIcon, "Afficher"], ["essentials", Zap, "Essentiels"], ["toolbox", Briefcase, "Boîte à outils"],
  ["files", ArrowLeftRight, "Transfert de fichiers"], ["capture", Camera, "Capture d'écran"], ["sound", Mic, "Son"],
  ["annotate", Pencil, "Annotation"], ["chat", MessageSquare, "Messages"], ["clipboard", ClipboardList, "Presse-papiers"], ["info", Info, "État"],
];

const Group = ({ title, children, cols = "grid-cols-3" }) => (
  <div className="bg-white/5 rounded-sm">
    <p className="px-2.5 py-1.5 text-xs font-semibold text-slate-200 bg-white/10">{title}</p>
    <div className={`grid ${cols} gap-1 p-1`}>{children}</div>
  </div>
);
const Tile = ({ icon: Icon, label, onClick, active, disabled, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`min-h-[64px] px-2 py-2 flex flex-col items-center justify-center gap-1 text-[11px] leading-tight text-center rounded-sm transition-colors disabled:opacity-40
      ${active ? "bg-sky-600 text-white ring-1 ring-sky-300" : "bg-white/10 text-slate-100 hover:bg-white/20"}`}>
    {Icon && <Icon className="h-4 w-4" />}<span>{label}</span>
  </button>
);

export default function Session() {
  const { id } = useParams();
  const [machine, setMachine] = useState(null);
  const [open, setOpen] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [quality, setQuality] = useState("high");
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  const toggleFull = () => (document.fullscreenElement ? document.exitFullscreen() : rootRef.current?.requestFullscreen?.())?.catch?.(() => {});
  const [blank, setBlank] = useState(false);
  const [annotate, setAnnotate] = useState(false);
  const [rec, setRec] = useState("idle");
  const [startedAt] = useState(Date.now());
  const [, tick] = useState(0);
  const rootRef = useRef(null);
  const recRef = useRef(null);
  const { canvasRef, state, messages, fps, res, monitors, send, subscribe, streamError } = useRemoteSession(id);
  const voice = useVoice(send, subscribe);
  const live = state === "live";
  const qSent = useRef(false);
  useEffect(() => { if (live && !qSent.current) { qSent.current = true; send({ type: "quality", level: quality }); } }, [live]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get(`/machines/${id}`).then(({ data }) => { setMachine(data); document.title = `${data.display_name || data.hostname} · ScreenView`; }).catch(() => {});
  }, [id]);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    const esc = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", esc);
    return () => { clearInterval(t); window.removeEventListener("keydown", esc); };
  }, []);
  useEffect(() => { if (messages.length && messages[messages.length - 1].sender === "user" && open !== "chat") toast("Nouveau message du client"); }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (k) => setOpen((c) => (c === k ? null : k));
  const setQ = (q) => { setQuality(q); send({ type: "quality", level: q }); };
  const doAction = (name, label) => { send({ type: "action", name }); toast.success(`${label} envoyé au poste distant`); };
  const toggleBlank = () => { const v = !blank; setBlank(v); send({ type: "blank", on: v }); };
  const toggleAnnotate = () => { const v = !annotate; setAnnotate(v); if (!v) send({ type: "annotate", op: "clear" }); };
  const blob = (cb) => {
    const c = canvasRef.current;
    if (!live || !c?.width) return toast.error("Aucune image à capturer");
    c.toBlob(cb, "image/png");
  };
  const download = (b, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = name; a.click(); URL.revokeObjectURL(a.href); };
  const shotFile = () => blob((b) => { download(b, `capture-${machine?.hostname || "ecran"}-${Date.now()}.png`); toast.success("Capture enregistrée"); });
  const shotClip = () => blob((b) => navigator.clipboard.write([new window.ClipboardItem({ "image/png": b })])
    .then(() => toast.success("Capture copiée dans le presse-papiers")).catch(() => toast.error("Copie impossible : utilisez « Vers le fichier »")));
  const recStart = () => {
    if (rec === "paused") { recRef.current.resume(); return setRec("recording"); }
    if (!live) return toast.error("Aucune image à enregistrer");
    const chunks = [];
    const mr = new MediaRecorder(canvasRef.current.captureStream(15), { mimeType: "video/webm" });
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => { download(new Blob(chunks, { type: "video/webm" }), `session-${machine?.hostname || "ecran"}-${Date.now()}.webm`); toast.success("Vidéo enregistrée"); };
    mr.start(1000);
    recRef.current = mr;
    setRec("recording");
  };
  const recPause = () => { recRef.current?.pause(); setRec("paused"); };
  const recStop = () => { recRef.current?.stop(); recRef.current = null; setRec("idle"); };
  const hangup = () => { if (recRef.current) recStop(); window.close() || window.location.assign("/app"); };
  const elapsed = Math.floor((Date.now() - startedAt) / 1000);

  const panels = {
    view: <div className="grid grid-cols-2 gap-1.5 w-[520px]">
      <div className="col-span-2"><Group title="Sélectionner les moniteurs" cols="grid-cols-4">
        {monitors.list.length ? monitors.list.map((m, i) => <Tile key={i} icon={MonitorIcon} label={`Écran ${i + 1} · ${m.width}×${m.height}`} active={monitors.current === i + 1} onClick={() => send({ type: "monitor", index: i + 1 })} testid={`monitor-item-${i + 1}`} />)
          : <Tile icon={MonitorIcon} label="Écran principal" active />}
      </Group></div>
      <Group title="Sélectionner la qualité">
        {[["low", "L", "Faible"], ["medium", "M", "Moyen"], ["high", "H", "Élevé"]].map(([v, l, t]) => <Tile key={v} label={<><b className="text-base block">{l}</b>{t}</>} active={quality === v} onClick={() => setQ(v)} testid={`quality-${v}`} />)}
      </Group>
      <Group title="Zoom" cols="grid-cols-3">
        <Tile label="−" onClick={() => setZoom((z) => Math.max(0.25, (z || 1) - 0.25))} testid="zoom-out" />
        <Tile label={zoom ? `${Math.round(zoom * 100)} %` : "Ajusté"} active onClick={() => setZoom(0)} testid="zoom-fit" />
        <Tile label="+" onClick={() => setZoom((z) => Math.min(3, (z || 1) + 0.25))} testid="zoom-in" />
        {[0.5, 0.75, 1, 1.5, 2].map((z) => <Tile key={z} label={`${z * 100} %`} active={zoom === z} onClick={() => setZoom(z)} testid={`zoom-${z * 100}`} />)}
      </Group>
    </div>,
    essentials: <div className="grid grid-cols-3 gap-1.5 w-[560px]">
      <Group title="Clavier et presse-papiers" cols="grid-cols-2">
        <Tile icon={Keyboard} label="Envoyer Ctrl-Alt-Suppr" onClick={() => doAction("ctrl_alt_del", "Ctrl+Alt+Suppr")} disabled={!live} testid="tool-cad" />
        <Tile icon={ClipboardPaste} label="Coller mon presse-papiers" disabled={!live} testid="tool-paste-clipboard"
          onClick={() => navigator.clipboard.readText().then((t) => { send({ type: "clipboard_set", text: t, paste: true }); toast.success("Texte collé sur le poste distant"); }).catch(() => setOpen("clipboard"))} />
      </Group>
      <Group title="Session" cols="grid-cols-2">
        <Tile icon={Lock} label="Verrouiller" onClick={() => doAction("lock", "Verrouillage")} disabled={!live} testid="tool-lock" />
        <Tile icon={RotateCcw} label="Redémarrer" onClick={() => doAction("restart", "Redémarrage")} disabled={!live} testid="tool-restart" />
      </Group>
      <Group title="Confidentialité" cols="grid-cols-1">
        <Tile icon={EyeOff} label={blank ? "Écran du client : noir" : "Rendre l'écran du client noir"} active={blank} onClick={toggleBlank} disabled={!live} testid="tool-blank" />
      </Group>
    </div>,
    toolbox: <div className="w-[460px] h-[440px] flex [&>aside]:w-full [&>aside]:border-0"><CommandPanel send={send} subscribe={subscribe} live={live} os={machine?.os} /></div>,
    files: <div className="w-[420px] h-[480px] flex [&>aside]:w-full [&>aside]:border-0"><FilesPanel send={send} subscribe={subscribe} live={live} /></div>,
    capture: <div className="grid grid-cols-2 gap-1.5 w-[440px]">
      <Group title="Prendre une capture" cols="grid-cols-2">
        <Tile icon={ClipboardList} label="Vers le presse-papiers" onClick={shotClip} disabled={!live} testid="capture-clipboard" />
        <Tile icon={Save} label="Vers le fichier" onClick={shotFile} disabled={!live} testid="menu-screenshot" />
      </Group>
      <Group title="Enregistrer la vidéo" cols="grid-cols-3">
        <Tile icon={Circle} label={rec === "paused" ? "Reprendre" : "Enregistrer"} active={rec === "recording"} onClick={recStart} disabled={!live || rec === "recording"} testid="capture-record" />
        <Tile icon={Pause} label="Interrompre" onClick={recPause} disabled={rec !== "recording"} testid="capture-pause" />
        <Tile icon={Square} label="Arrêter" onClick={recStop} disabled={rec === "idle"} testid="capture-stop" />
      </Group>
    </div>,
    sound: <div className="w-[360px] p-1 text-slate-100 [&_button]:text-slate-100" data-testid="panel-sound-content"><p className="text-xs text-slate-400 mb-2">Appel vocal avec le client. Choisissez le micro et les haut-parleurs avec l'icône réglages.</p><AudioControls voice={voice} /></div>,
    annotate: <div className="grid grid-cols-2 gap-1.5 w-[360px]">
      <Group title="Mode" cols="grid-cols-1"><Tile icon={Pencil} label={annotate ? "Annotation active : dessinez" : "Activer l'annotation"} active={annotate} onClick={toggleAnnotate} disabled={!live} testid="tool-annotate" /></Group>
      <Group title="Effacer" cols="grid-cols-1"><Tile icon={Eraser} label="Effacer les dessins" onClick={() => send({ type: "annotate", op: "clear" })} disabled={!annotate} testid="annotate-clear-btn" /></Group>
    </div>,
    chat: <div className="w-[380px] h-[420px] flex [&>aside]:w-full [&>aside]:border-0"><ChatPanel messages={messages} onSend={(text) => send({ type: "chat", text })} /></div>,
    clipboard: <div className="w-[340px] max-h-[520px] overflow-auto flex [&>aside]:w-full [&>aside]:border-0"><ClipboardPanel send={send} subscribe={subscribe} live={live} /></div>,
    info: <table className="w-[360px] text-xs text-slate-200 border border-white/10" data-testid="panel-info-table"><tbody>
      {[["État de la connexion", STATE_LABEL[state]], ["Poste", machine?.display_name || machine?.hostname], ["Système", machine?.os_version || machine?.os],
        ["Utilisateur", machine?.username], ["Durée de la connexion", `${Math.floor(elapsed / 60)} min ${elapsed % 60} s`], ["Résolution", res || "—"],
        ["Images / seconde", fps || 0], ["Qualité", { low: "Faible", medium: "Moyenne", high: "Élevée" }[quality]], ["Messages échangés", messages.length]]
        .map(([k, v]) => <tr key={k} className="border-b border-white/5"><td className="px-3 py-1.5 text-slate-400">{k}</td><td className="px-3 py-1.5">{v ?? "—"}</td></tr>)}
    </tbody></table>,
  };

  return (
    <div ref={rootRef} className="h-screen flex flex-col bg-[#2b2b2b] select-none" data-testid="remote-viewer">
      <header className="relative h-9 flex items-center bg-[#3a3a3a] text-slate-200 px-3 z-30">
        <p className="text-xs font-medium truncate max-w-[30%]" data-testid="session-machine-name">{machine?.display_name || machine?.hostname || "…"} — ScreenView</p>
        <nav className="absolute left-1/2 -translate-x-1/2 top-0 h-9 flex items-center gap-0.5 px-6 bg-[#4a4a4a] [clip-path:polygon(0_0,100%_0,calc(100%-18px)_100%,18px_100%)]" data-testid="session-toolbar">
          {TOOLS.map(([k, Icon, label]) => (
            <button key={k} title={label} onClick={() => toggle(k)} data-testid={`tb-${k}`}
              className={`h-8 w-9 grid place-items-center rounded-sm transition-colors ${open === k ? "bg-sky-600 text-white" : "hover:bg-white/15"}
                ${(k === "annotate" && annotate) || (k === "sound" && voice.active) || (k === "capture" && rec !== "idle") ? "text-sky-300" : ""}`}>
              <Icon className="h-[18px] w-[18px]" />
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <span data-testid="session-state" className={`text-[11px] mr-2 ${live ? "text-emerald-400" : "text-amber-400"}`}>{live ? `● ${res} · ${fps} img/s` : STATE_LABEL[state]}</span>
          <button title={full ? "Quitter le plein écran" : "Plein écran"} onClick={toggleFull} className="h-7 w-8 grid place-items-center hover:bg-white/15 rounded-sm" data-testid="remote-fullscreen-btn">{full ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}</button>
          <button title="Raccrocher" onClick={hangup} className="h-7 w-8 grid place-items-center hover:bg-red-600 rounded-sm" data-testid="remote-disconnect-btn"><PhoneOff className="h-4 w-4" /></button>
        </div>
      </header>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-9 z-40 bg-[#262626]/95 backdrop-blur border border-black shadow-2xl p-3 pt-2" data-testid={`panel-${open}`}>
          <div className="flex items-center mb-2">
            <p className="text-lg font-semibold text-white mr-auto">{TOOLS.find((t) => t[0] === open)[2]}</p>
            <button onClick={() => setOpen(null)} className="text-slate-300 hover:text-white" data-testid="panel-close"><X className="h-4 w-4" /></button>
          </div>
          {panels[open]}
        </div>
      )}
      <div className="flex-1 min-h-0 flex items-center justify-center relative" onMouseDown={() => open && !["chat", "files", "toolbox", "clipboard", "sound"].includes(open) && setOpen(null)}>
        <RemoteCanvas canvasRef={canvasRef} send={send} subscribe={subscribe} visible={live} zoom={zoom} annotate={annotate} />
        {annotate && live && <span className="absolute bottom-3 left-3 text-xs bg-red-500/90 text-white px-2 py-1 rounded" data-testid="annotate-badge">Mode annotation — dessinez sur l'écran du client</span>}
        {rec !== "idle" && <span className="absolute bottom-3 right-3 text-xs bg-red-600 text-white px-2 py-1 rounded animate-pulse" data-testid="recording-badge">● {rec === "paused" ? "Enregistrement en pause" : "Enregistrement"}</span>}
        {!live && (
          <div className="text-center text-slate-400" data-testid="session-placeholder">
            {["connecting", "waiting"].includes(state) ? <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-sky-400" /> : <WifiOff className="h-8 w-8 mx-auto mb-3" />}
            <p>{MESSAGES[state]}</p>
            {state === "waiting" && streamError && (
              <p className="mt-3 max-w-lg mx-auto text-xs text-amber-300" data-testid="session-stream-error">
                Le poste n'arrive pas à capturer son écran ({streamError}). Si la session Windows est verrouillée, essayez Ctrl+Alt+Suppr depuis la barre d'outils.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
